import { defaultEffect } from '../../format/helpers';
import type { Effect, EffectType, VideoClip } from '../../format/types';
import { useTimeline } from '../../state/timeline';
import ParamSlider from './ParamSlider';

const EFFECT_TYPES: EffectType[] = [
  'brightness',
  'contrast',
  'saturation',
  'hue',
  'grayscale',
  'sepia',
  'invert',
  'blur',
];

export const EFFECT_RANGES: Record<EffectType, { min: number; max: number; step: number }> = {
  brightness: { min: -1, max: 1, step: 0.01 },
  contrast: { min: 0, max: 2, step: 0.01 },
  saturation: { min: 0, max: 2, step: 0.01 },
  hue: { min: -180, max: 180, step: 1 },
  grayscale: { min: 0, max: 1, step: 0.01 },
  sepia: { min: 0, max: 1, step: 0.01 },
  invert: { min: 0, max: 1, step: 0.01 },
  blur: { min: 0, max: 50, step: 0.5 },
};

function effectField(e: Effect): 'degrees' | 'radius' | 'amount' {
  if (e.type === 'hue') return 'degrees';
  if (e.type === 'blur') return 'radius';
  return 'amount';
}

function effectParam(e: Effect) {
  if (e.type === 'hue') return e.degrees;
  if (e.type === 'blur') return e.radius;
  return e.amount;
}

function withEffectValue(e: Effect, v: number): Effect {
  if (e.type === 'hue') return { ...e, degrees: v };
  if (e.type === 'blur') return { ...e, radius: v };
  return { ...e, amount: v };
}

export default function EffectsEditor({
  clip,
  onSeek,
}: {
  clip: VideoClip;
  onSeek?: (t: number) => void;
}) {
  const update = useTimeline((s) => s.updateClip);
  const setEffects = (effects: Effect[]) => update(clip.id, { effects });

  return (
    <div className="insp-section">
      <h3>Effects</h3>
      {clip.effects.map((e, i) => {
        const range = EFFECT_RANGES[e.type];
        return (
          <div className="insp-effect" key={e.id}>
            <div className="insp-effect-head">
              <span>{e.type}</span>
              <span className="insp-effect-btns">
                <button
                  className="ghost icon"
                  disabled={i === 0}
                  title="Move up"
                  onClick={() => {
                    const fx = [...clip.effects];
                    [fx[i - 1], fx[i]] = [fx[i], fx[i - 1]];
                    setEffects(fx);
                  }}
                >
                  ↑
                </button>
                <button
                  className="ghost icon"
                  disabled={i === clip.effects.length - 1}
                  title="Move down"
                  onClick={() => {
                    const fx = [...clip.effects];
                    [fx[i], fx[i + 1]] = [fx[i + 1], fx[i]];
                    setEffects(fx);
                  }}
                >
                  ↓
                </button>
                <button
                  className="ghost icon"
                  title="Remove"
                  onClick={() => setEffects(clip.effects.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </span>
            </div>
            <ParamSlider
              clip={clip}
              path={`effects.${e.id}.${effectField(e)}`}
              param={effectParam(e)}
              label={effectField(e)}
              min={range.min}
              max={range.max}
              step={range.step}
              onStatic={(v) => {
                const fx = [...clip.effects];
                fx[i] = withEffectValue(e, v);
                setEffects(fx);
              }}
              onSeek={onSeek}
            />
          </div>
        );
      })}
      <select
        value=""
        onChange={(e) => {
          if (!e.target.value) return;
          setEffects([...clip.effects, defaultEffect(e.target.value as EffectType)]);
        }}
      >
        <option value="">+ add effect…</option>
        {EFFECT_TYPES.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
    </div>
  );
}
